import { HttpClient } from '../../../services/api/client'
import { AuthGuard } from '../../../services/auth/authGuard'
import type { StakeholderTemplate, ProjectStakeholder, ApiResponse } from '@bandos/shared'

export interface StakeholderTemplatesResponse {
  public: StakeholderTemplate[]
  private: StakeholderTemplate[]
}

/**
 * Service for managing stakeholder templates and project stakeholders
 */
export class StakeholderService {
  /**
   * Get all templates (public + user's private)
   */
  static async getTemplates(): Promise<ApiResponse<StakeholderTemplatesResponse>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<StakeholderTemplatesResponse>('/stakeholders/templates', auth.headers)
  }

  /**
   * Create a new template
   */
  static async createTemplate(
    templateData: Omit<
      StakeholderTemplate,
      'created_at' | 'updated_at' | 'is_public' | 'created_by_user_id'
    > & {
      id: string
    }
  ): Promise<ApiResponse<StakeholderTemplate>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.post<StakeholderTemplate>(
      '/stakeholders/templates',
      templateData,
      auth.headers
    )
  }

  /**
   * Update a template
   */
  static async updateTemplate(
    templateId: string,
    templateData: Partial<
      Omit<
        StakeholderTemplate,
        'id' | 'created_at' | 'updated_at' | 'is_public' | 'created_by_user_id'
      >
    >
  ): Promise<ApiResponse<StakeholderTemplate>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.put<StakeholderTemplate>(
      `/stakeholders/templates/${templateId}`,
      templateData,
      auth.headers
    )
  }

  /**
   * Delete a template
   */
  static async deleteTemplate(templateId: string): Promise<ApiResponse<{ success: boolean }>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.delete<{ success: boolean }>(
      `/stakeholders/templates/${templateId}`,
      auth.headers
    )
  }

  /**
   * Get project stakeholders
   */
  static async getProjectStakeholders(
    mindmapId: string
  ): Promise<ApiResponse<ProjectStakeholder[]>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<ProjectStakeholder[]>(`/stakeholders/projects/${mindmapId}`, auth.headers)
  }

  /**
   * Add stakeholder to project
   */
  static async addStakeholderToProject(
    mindmapId: string,
    templateId: string
  ): Promise<ApiResponse<ProjectStakeholder>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.post<ProjectStakeholder>(
      `/stakeholders/projects/${mindmapId}`,
      { templateId },
      auth.headers
    )
  }

  /**
   * Remove stakeholder from project
   */
  static async removeStakeholderFromProject(
    mindmapId: string,
    stakeholderId: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.delete<{ success: boolean }>(
      `/stakeholders/projects/${mindmapId}/${stakeholderId}`,
      auth.headers
    )
  }
}
